import {  useState } from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { validateMobileNumber } from "../utils/MobileNoValidation";
import { axiosFunction } from "../components/commonFunctions";
import { v2ReactURL } from "../utils/constant";

const useContactValidation = (contactId:number) => {
  const [lastValidatedMobNo, setLastValidatedMobNo] = useState  <string | null>(null);
  const [isLastValid, setIsLastValid] = useState<boolean | undefined>(undefined);
  const [lastValidatedCode, setLastValidatedCode] = useState  <string | null>(null);
  const [isLastValidCode, setIsLastValidCode] = useState<boolean | undefined>(undefined);
  const phoneNo = useSelector((state:any) => state.phoneNo);
  const contactType = useSelector((state:any) => state.contactType);

  const emailRegex =
    /^(?!.*[^A-Za-z0-9]{2})[A-Za-z0-9]\.*(?=[A-Za-z][a-zA-Z0-9@._-]{5,50}$)[a-zA-Z0-9._-]{1,40}@(?:(?=[a-zA-Z][a-zA-Z0-9-]{1,15}\.)[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\.){1,2}[a-zA-Z]{2,6}$/;

  const panNoRegex = /[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}/;

  const tanNoRegex = /^[A-Z]{4}\d{5}[A-Z]$/;

  // const prevMobileNumberRef = useRef("");

  const addressSchema = yup.object().shape({
    contactFirstName:
    contactType === "suppliers"
    ? yup
    .string()
    .nullable()
    .optional()
    .max(50, "Only 50 characters allowed ")
    .when("$firstName", (firstNameValue, schema) => {
      return firstNameValue
        ? schema.test(
            "is-valid-firstName",
            "The First Name can only consist of  alphabets, numbers, dot, comma, braces, underscore, at sign, dollar, and ampersand.",
            (value) => {
              // Allow null or undefined for optional fields
              if (value === null || value === undefined || value === "")
                return true;
              // Validate against the pattern
              return /^(?:[a-zA-Z0-9()_@$\s-.,&]|&amp;)+$/.test(value);
            }
          )
        : schema;
    })
      : yup
      .string()
      .trim()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .required("The First Name is required")
      .when("$firstName", (firstNameValue, schema) => {
        return firstNameValue
          ? schema.test(
              "is-valid-firstName",
              "The First Name can only consist of  alphabets, numbers, dot, comma, braces, underscore, at sign, dollar, and ampersand.",
              (value) => {
                // Allow null or undefined for optional fields
                if (value === null || value === undefined || value === "")
                  return true;
                // Validate against the pattern
                return /^(?:[a-zA-Z0-9()_@$\s-.,&]|&amp;)+$/.test(value);
              }
            )
          : schema;
          }),

    contactLastName: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-lastName",
        "The Last Name can only consist of alphabetical,number,dot,comma,braces , underscore, at sign and dollar.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9()_@$\s-., ]+$/.test(value);
        }
      ),
      debitAmount: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-lastName",
        "The Last Name can only consist of alphabetical,number,dot,comma,braces , underscore, at sign and dollar.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9()_@$\s-., ]+$/.test(value);
        }
      ),
    contactCompanyName:
      contactType === "suppliers"
        ? yup
            .string()
            .trim()
            .max(75, "The Company Name must be less than 75 characters long")
            .required("The Company Name is required")
            .test(
              "is-valid-companyName",
              `The Company Name can only consist of alphanumeric and all special character except "<>/':-=()~;`,
              function (value) {
                if (value === null || value === undefined || value === "")
                  return true;
                return /^[a-zA-Z0-9\s!#$%&;*+,.?@\[\]\\^_{}|`-]+$/.test(value);
              }
            )
        : yup
            .string()
            .nullable()
            .optional()
            .max(75, "The Company Name must be less than 75 characters long")
            .test(
              "is-valid-companyName",
              `The Company Name can only consist of alphanumeric and all special character except "<>/':-=()~;`,
              function (value) {
                if (value === null || value === undefined || value === "")
                  return true;
                return /^[a-zA-Z0-9\s!#$%&;*+,.?@\[\]\\^_{}|`-]+$/.test(value);
              }
            ),
    contactEmail: yup
      .string()
      .optional()
      .nullable()
      .max(50, "Email Address should not Greater than 50.")
      .test("is-valid-email", "Email address is not a valid", function (value) {
        if (value === null || value === undefined || value === "") return true;
        return emailRegex.test(value);
      }),

    pincode: yup
      .string()
      .optional()
      .nullable()
      .test(
        "is-valid-pincode",
        "The ZIP/Postal code must be between 6 to 8 alphanumeric.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[A-Za-z0-9]{6,8}$/.test(value);
        }
      ),

    countriesCode: yup.object().required("Select Country"),
    state: yup.object().required("Select State"),
    city: yup.object().required("Select City"),

    addressLine1: yup
      .string()
      .nullable()
      .optional()
      .max(250, "The Address Line 1 must be less than 250 characters long"),
    addressLine2: yup
      .string()
      .nullable()
      .optional()
      .max(250, "The Address must be less than 250 characters long"),

    GSTIN: yup
      .string()
      .nullable()
      .test("GSTIN", (value, root) => {
        const addDetails =  root?.from && root?.from[1]?.value?.addressDetails;
        const index = root?.path.replace(/[^\d]/g, "");

        if (!addDetails || index === undefined) {
          return true;
        }

        const gstType = addDetails[index]?.gstType?.value;
        if (gstType === "UnRegistered" || (gstType && gstType.toString().toLowerCase() === "vat")) {
          return true;
        } else {
          if (!value) {
            return root.createError({
              message: `The GSTIN Should Not Be Empty`,
              path: root?.path,
            });
          } else if (
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(
              value
            )
          ) {
            return true;
          } else {
            return root.createError({
              message: `GSTIN/UIN is not more than 15 digit long. The GSTIN/UIN that you have entered is invalid. Please enter a valid GSTIN/UIN`,
              path: root?.path,
            });
          }
        }
      }),

    mobNo: yup
      .string()
      .nullable()
      .optional()
      .test("mobilenumber", (value, root) => {
        if (!value) {
          return true;
        }

        const index = root?.path.replace(/[^\d]/g, "");

        const name = `addressDetailsmobNo[${index}]`;
        const prefix = phoneNo?.prefixes[name];

        if (prefix == "91" || prefix === undefined) {
          if (!/^\d{10}$/.test(value)) {
            return root.createError({
              message: "Invalid Mobile no.",
              path: root?.path,
            });
          }
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            /^(\+91[\-\s]?)?[0]?[6789]\d{9}$/.test(value)
          ) {
            return true;
          } else {
            return root.createError({
              message: `Invalid Mobile no.`,
              path: root?.path,
            });
          }
        } else {
          if (!/^\d{3,15}$/.test(value)) {
            return root.createError({
              message: "Invalid Mobile no.",
              path: root?.path,
            });
          }
          return true;
        }
      }),
  });

  const mobNoValidation =
  phoneNo.prefixes.mobNo == "91"
    ? yup
        .string()
        .nullable()
        .optional()
        .test("is-numeric", "Only numbers are allowed", function (value) {
          if (value === null || value === undefined || value === "") return true;
          return /^\d+$/.test(value); // Ensure only digits are allowed
        })
        .test("is-valid-mobno", "Invalid Mobile no.", function (value) {
          if (value === null || value === undefined || value === "") return true;
          // Validate Indian mobile numbers (10 digits, starting with 6,7,8,9)
          return /^[6789]\d{9}$/.test(value);
        })
        .test("mobNo", "This Mobile Number already exists.", async function (value) {
          if (!value) return true;

          if (value === lastValidatedMobNo && isLastValid !== undefined) {
            return isLastValid;
          }

          try {
            const res = await validateMobileNumber(value, contactType, contactId);
            const isValid = !(res?.data?.message === "Mobile Number already Exist");

            setLastValidatedMobNo(value);
            setIsLastValid(isValid);

            return isValid || this.createError({ message: "This Mobile Number already exists." });
          } catch (error) {
            console.error("Error validating mobile number:", error);
            return this.createError({ message: "Validation error. Please try again." });
          }
        })
    : yup
        .string()
        .nullable()
        .optional()
        .test("is-numeric", "Only numbers are allowed", function (value) {
          if (value === null || value === undefined || value === "") return true;
          return /^\d+$/.test(value); // Ensure only digits are allowed
        })
        .test("is-valid-mobno", "Invalid Mobile no.", function (value) {
          if (value === null || value === undefined || value === "") return true;
          // Validate international numbers (3 to 15 digits)
          return value.length >= 3 && value.length <= 15;
        })
        .test("mobNo", "This Mobile Number already exists.", async function (value) {
          if (!value) return true;

          if (value === lastValidatedMobNo && isLastValid !== undefined) {
            return isLastValid;
          }

          try {
            const res = await validateMobileNumber(value, contactType, contactId);
            const isValid = !(res?.data?.message === "Mobile Number already Exist");

            setLastValidatedMobNo(value);
            setIsLastValid(isValid);

            return isValid || this.createError({ message: "This Mobile Number already exists." });
          } catch (error) {
            console.error("Error validating mobile number:", error);
            return this.createError({ message: "Validation error. Please try again." });
          }
        })

const whatsappNoValidation =
  phoneNo.prefixes.whatsappNo == "91"
    ? yup
        .string()
        .nullable()
        .optional()
        .test("is-numeric", "Only numbers are allowed", function (value) {
          if (value === null || value === undefined || value === "") return true;
          return /^\d+$/.test(value); // Ensure only digits are allowed
        })
        .test("is-valid-whatsapp", "Invalid Mobile no.", function (value) {
          if (value === null || value === undefined || value === "") return true;
          // Validate Indian mobile numbers (10 digits, starting with 6,7,8,9)
          return /^[6789]\d{9}$/.test(value);
        })
    : yup
        .string()
        .nullable()
        .optional()
        .test("is-numeric", "Only numbers are allowed", function (value) {
          if (value === null || value === undefined || value === "") return true;
          return /^\d+$/.test(value); // Ensure only digits are allowed
        })
        .test("is-valid-whatsapp", "Invalid Mobile no.", function (value) {
          if (value === null || value === undefined || value === "") return true;
          // Validate international numbers (3 to 15 digits)
          return value.length >= 3 && value.length <= 15;
        });
  const CustomerValidationSchema = yup.object().shape({
    code: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Code Should not be greater than 50")
      .test("validate-code", "This Code already exists", async (value) => {
        if (!value) return true; // Skip validation if empty
  
        // **Check if code was already validated to avoid redundant API calls**
        if (value === lastValidatedCode) {
          return isLastValidCode; // Use cached validation result
        }
  
        try {
          const response = await axiosFunction(
            `${v2ReactURL}/contact/${contactType}/checkcode?code=${encodeURIComponent(value)}&contactId=${contactId}`,
            "POST"
          );
  
          const isValid = response.data?.valid ?? false;
  
          // **Update state with the last validated code and its validity**
          setLastValidatedCode(value);
          setIsLastValidCode(isValid);
  
          return isValid;
        } catch (error) {
          console.error("Error validating code:", error);
          return false; // Fails validation on API error
        }
      }),
    mobNo: mobNoValidation,
    whatsappNo: whatsappNoValidation,

    firstName: yup
      .string()
      .trim()
      .max(50, "Only 50 characters allowed ")
      .required("The First Name is required")

      .test(
        "is-valid-firstname",
         "The First Name can only consist of  alphabets, numbers, dot, comma, braces, underscore, at sign, dollar, and ampersand.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^(?:[a-zA-Z0-9()_@$\s-.,&]|&amp;)+$/.test(value);
        }
      ),
    lastName: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-lastname",
        "The Last Name can only consist of alphabetical,number,dot,comma,braces , underscore, at sign and dollar.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9()_@$\s-., ]+$/.test(value);
        }
      ),
    companyName: yup
      .string()
      .nullable()
      .optional()
      .max(75, "The Company Name must be less than 75 characters long")
      .test(
        "is-valid-companyName",
        "The Company Name can only consist of alphanumeric and all special character except \"<>/':-=()~;",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9\s!#$%&;*+,.?@\[\]\\^_{}|`-]+$/.test(value);
        }
      ),
    remark: yup
      .string()
      .nullable()
      .optional()
      .max(200, "The remark must be less than 200 characters long"),
    email: yup
      .string()
      .optional()
      .nullable()
      .max(50, "Email Address should not Greater than 50.")
      .test("is-valid-email", "Email address is not a valid", function (value) {
        if (value === null || value === undefined || value === "") return true;
        return emailRegex.test(value);
      }),
      debitAmount : yup
      .string()
      .nullable()
      .test("debitAmount", "Opening balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined|| value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),
      creditAmount : yup
      .string()
      .nullable()
      .optional()
      .test("creditAmount", "Closing balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined|| value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),

    panNo: yup
      .string()
      .nullable()
      .optional()
      .max(10, "The pan no. must be 10 characters long")
      .test("is-valid-panno", "PAN No. is not valid", function (value) {
        // Return true for null or undefined (optional field)
        if (value === null || value === undefined || value === "") return true;
        // Check for 6 to 8 alphanumeric characters
        return panNoRegex.test(value);
      }),
      vatNo: yup
      .string()
      .nullable()
      .optional()
      .max(15, "The Vat No. must be less than 15 characters long"),
    telephone: yup
      .string()
      .nullable()
      .optional()
      .test(
        "len",
        "The Telephone No. Must be 11 digit number",
        function (value) {
          // Return true for null or undefined (optional field)
          if (!value || value.length === 11) {
            return true;
          } else {
            return false;
          }
        }
      ),

    addressDetails: yup.array().of(addressSchema),
  });

  const SupplierValidationSchema = yup.object().shape({
    code: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Code Should not be greater than 50")
      .test("validate-code", "This Code already exists", async (value) => {
        if (!value) return true; // Skip validation if empty
  
        // **Check if code was already validated to avoid redundant API calls**
        if (value === lastValidatedCode) {
          return isLastValidCode; // Use cached validation result
        }
  
        try {
          const response = await axiosFunction(
            `${v2ReactURL}/contact/${contactType}/checkcode?code=${encodeURIComponent(value)}&contactId=${contactId}`,
            "POST"
          );
  
          const isValid = response.data?.valid ?? false;
  
          // **Update state with the last validated code and its validity**
          setLastValidatedCode(value);
          setIsLastValidCode(isValid);
  
          return isValid;
        } catch (error) {
          console.error("Error validating code:", error);
          return false; // Fails validation on API error
        }
      }),

    mobNo: mobNoValidation,
    whatsappNo: whatsappNoValidation,

    firstName: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-firstName",
         "The First Name can only consist of alphabets, numbers, dot, comma, braces, underscore, at sign, dollar, and ampersand.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^(?:[a-zA-Z0-9()_@$\s-.,&]|&amp;)+$/.test(value);
        }
      ),
    lastName: yup
      .string()

      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-lastname",
        "The Last Name can only consist of alphabetical,number,dot,comma,braces , underscore, at sign and dollar.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9()_@$\s-., ]+$/.test(value);
        }
      ),
    companyName: yup
      .string()
      .trim()
      .max(75, "The Company Name must be less than 75 characters long")
      .required("The Company Name is required")
      .test(
        "is-valid-companyName",
        "The Company Name can only consist of alphanumeric and all special character except \"<>/':-=()~;",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9\s!#$%&;*+,.?@\[\]\\^_{}|`-]+$/.test(value);
        }
      ),
      debitAmount : yup
      .string()
      .nullable()
      .test("debitAmount", "Opening balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined|| value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),
      creditAmount : yup
      .string()
      .nullable()
      .optional()
      .test("creditAmount", "Closing balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined|| value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),
    remark: yup
      .string()
      .nullable()
      .optional()
      .max(200, "The remark must be less than 200 characters long"),
    email: yup
      .string()
      .optional()
      .nullable()
      .max(50, "Email Address should not Greater than 50.")
      .test("is-valid-email", "Email address is not a valid", function (value) {
        if (value === null || value === undefined || value === "") return true;
        return emailRegex.test(value);
      }),
    panNo: yup
      .string()
      .nullable()
      .optional()
      .max(10, "The pan no. must be 10 characters long")
      .test("is-valid-panno", "PAN No. is not valid", function (value) {
        // Return true for null or undefined (optional field)
        if (value === null || value === undefined || value === "") return true;
        // Check for 6 to 8 alphanumeric characters
        return panNoRegex.test(value);
      }),
      vatNo: yup
      .string()
      .nullable()
      .optional()
      .max(15, "The Vat No. must be less than 15 characters long"),
    telephone: yup
      .string()
      .nullable()
      .optional()
      .test(
        "len",
        "The Telephone No. Must be 11 digit number",
        function (value) {
          // Return true for null or undefined (optional field)
          if (!value || value.length === 11) {
            return true;
          } else {
            return false;
          }
        }
      ),
    tanNo: yup
      .string()
      .nullable()
      .optional()
      .max(10, "The TAN no. must be 10 characters long")
      .test("is-valid-tanNo", "TAN No. is not valid", function (value) {
        // Return true for null or undefined (optional field)
        if (value === null || value === undefined || value === "") return true;
        // Check for 6 to 8 alphanumeric characters
        return tanNoRegex.test(value);
      }),
    bankIfsc: yup
      .string()
      .optional()
      .nullable()
      .max(
        11,
        "It should be 11 characters long and contain only letters and numbers."
      )
      .test(
        "is-valid-ifsccode",
        "Invalid IFSC code. It should be 11 characters long and contain only letters and numbers.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[A-Za-z]{4}[a-zA-Z0-9]{7}$/.test(value);
        }
      ),
    bankName: yup
      .string()
      .optional()
      .nullable()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-bank-name-valid",
        "Only letters, numbers, spaces, underscores, commas, periods, and ampersands are allowed.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return  /^[a-zA-Z0-9_\s.,& ]+$/.test(value);
        }
      ),
    bankBranch: yup
      .string()
      .optional()
      .nullable()
      .max(100, "The branch name must be less than 100 characters long")
      .test(
        "is-bank-branch-valid",
        "Only letters, numbers, spaces, underscores, commas, periods, and ampersands are allowed.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return  /^[a-zA-Z0-9_\s.,& ]+$/.test(value);
        }
      ),
    bankAcNo: yup
      .string()
      .optional()
      .nullable()
      .max(18, "The bank account no. must be less than 18 characters long")
      .test(
        "is-valid-bankAccNo",
        "The bank account no. can only alphabetical and number",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[A-Za-z0-9\s]+$/.test(value);
        }
      ),
    addressDetails: yup.array().of(addressSchema),
  });

  const TransportValidationSchema = yup.object().shape({
    transporterId: yup
      .string()
      .nullable()
      .optional()
      .max(20, "The Transporter id must be less than 20 Characters."),
    code: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Code Should not be greater than 50"),

    mobNo: mobNoValidation,
    firstName: yup
      .string()
      .trim()
      .max(50, "Only 50 characters allowed ")
      .required("The First Name is required")
      .test(
        "is-valid-firstname",
         "The First Name can only consist of  alphabets, numbers, dot, comma, braces, underscore, at sign, dollar, and ampersand.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^(?:[a-zA-Z0-9()_@$\s-.,&]|&amp;)+$/.test(value);
        }
      ),
    lastName: yup
      .string()
      .nullable()
      .optional()
      .max(50, "Only 50 characters allowed ")
      .test(
        "is-valid-lastname",
        "The Last Name can only consist of alphabetical,number,dot,comma,braces , underscore, at sign and dollar.",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9()_@$\s-., ]+$/.test(value);
        }
      ),
    companyName: yup
      .string()
      .trim()
      .max(75, "The Company Name must be less than 75 characters long")
      .required("The Company Name is required")
      .test(
        "is-valid-companyname",
        "The Company Name can only consist of alphanumeric and all special character except \"<>/':-=()~;",
        function (value) {
          // Return true for null or undefined (optional field)
          if (value === null || value === undefined || value === "")
            return true;
          // Check for 6 to 8 alphanumeric characters
          return /^[a-zA-Z0-9\s!#$%&;*+,.?@\[\]\\^_{}|`-]+$/.test(value);
        }
      ),
      debitAmount : yup
      .string()
      .nullable()
      .test("debitAmount", "Opening balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined || value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),
      creditAmount : yup
      .string()
      .nullable()
      .optional()
      .test("creditAmount", "Closing balance cannot be more than 10 digits before decimal and 6 digits after decimal ", (value) => {
        if (value === null || value === undefined|| value==="") return true;
        if (typeof value === "string" && value.includes(".")) {
          const valueArr = value.split(".");
          return (valueArr[0]?.length ?? 0) <= 10 && (valueArr[1]?.length ?? 0) <= 6;
        } else {
          return value.length <= 10;
        }
      }),
    remark: yup
      .string()
      .nullable()
      .optional()
      .max(200, "The remark must be less than 200 characters long"),
    email: yup
      .string()
      .optional()
      .nullable()
      .max(50, "Email Address should not Greater than 50.")
      .test("is-valid-email", "Email address is not a valid", function (value) {
        if (value === null || value === undefined || value === "") return true;
        return emailRegex.test(value);
      }),
    panNo: yup
      .string()
      .nullable()
      .optional()
      .max(10, "The pan no. must be 10 characters long")
      .test("is-valid-panno", "PAN No. is not valid", function (value) {
        // Return true for null or undefined (optional field)
        if (value === null || value === undefined || value === "") return true;
        // Check for 6 to 8 alphanumeric characters
        return panNoRegex.test(value);
      }),
      vatNo: yup
      .string()
      .nullable()
      .optional()
      .max(15, "The Vat No. must be less than 15 characters long"),
    telephone: yup
      .string()
      .nullable()
      .optional()
      .test(
        "len",
        "The Telephone No. Must be 11 digit number",
        function (value) {
          // Return true for null or undefined (optional field)
          if (!value || value.length === 11) {
            return true;
          } else {
            return false;
          }
        }
      ),
    addressDetails: yup.array().of(addressSchema),
  });

  return {
    CustomerValidationSchema,
    SupplierValidationSchema,
    TransportValidationSchema,
  };
};

export default useContactValidation;
